<template>
  <v-container
    fluid
  >
    <!-- Graph -->
    <v-subheader>Temperatura</v-subheader>
    <v-row class="ma-0">
      <v-col
        cols="12"
      >
        <apexcharts
          type="line"
          :height="400"
          :options="chartOptions"
          :series="series"
        />
      </v-col>
    </v-row>
    
    <!-- Table -->
    <v-row class="ma-0">
      <v-col
        cols="12"
      >
        <v-data-table
          dense
          :headers="tableHeaders"
          disable-sort
          no-results-text="Nenhum registro encontrado"
          :items="filteredProbeDataDesc"
          color="secondary"
          loading-text="Carregando registros...aguarde"
        >
          <template v-slot:item.tsProbeRegistry="{ item }">
            <span class="caption">{{ item["tsProbeRegistry"] | dateFormateDisplay }}</span>
          </template>
          <template v-slot:item.strAmbienceName="{ item }">
            <span class="caption">{{ item["strAmbienceName"] | tempNameDisplay }}</span>
          </template>
          <template v-slot:item.color="{ item }">
            <v-chip
              x-small
              :color="getTempColor(item['strAmbienceName'])"
            />
          </template>
        </v-data-table>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import apexcharts from "vue-apexcharts"
import {forEach, includes} from "lodash/collection"
import {uniq, uniqBy} from "lodash/array"
import moment from "moment"

export default {
  name: "AmbienceTempGraph",
  components: {
    apexcharts
  },
  props: {
    probeData: {
      type: Array,
      required: true
    }
  },
  filters: {
    tempNameDisplay(value) {
      switch (value) {
        case "TE":
          return "Esquerda"
        case "TD":
          return "Direita"
        case "T_EXTERNA":
          return "Externa"
        case "T_INTERNA":
          return "Interna"
        default:
          return ""
      }
    },
    dateFormateDisplay(value) {
      const parsedDate = moment(value)
      if (!parsedDate.isValid()) return ""
      return parsedDate.format("HH:mm - DD/MM/Y")
    }
  },
  computed: {
    filteredProbeData() {
      return this.probeData.filter((item) => {
        return (includes(["TE", "TD", "T_EXTERNA", "T_INTERNA"], item["strAmbienceName"]))
      }).sort((a, b) => new Date(a["tsProbeRegistry"]) - new Date(b["tsProbeRegistry"]))
    },
    filteredProbeDataDesc() {
      return this.probeData.filter((item) => {
        return (includes(["TE", "TD", "T_EXTERNA", "T_INTERNA"], item["strAmbienceName"]))
      }).sort((a, b) => new Date(b["tsProbeRegistry"]) - new Date(a["tsProbeRegistry"]))
    },
    
    tempMaxSeries() {
      return uniqBy(this.filteredProbeData, 'tsProbeRegistry').map(item => item["flProbeMax"])
    },
    tempMinSeries() {
      return uniqBy(this.filteredProbeData, 'tsProbeRegistry').map(item => item["flProbeMin"])
    },
    tempLeftSeries() {
      const tempList = []
      let previousPoint = 0
      const uniqueList = uniqBy(this.filteredProbeData, 'tsProbeRegistry')
      forEach(uniqueList, (item) => {
        if (item["strAmbienceName"] === "TE") {
          previousPoint = item["flProbeAvg"]
          tempList.push(item["flProbeAvg"])
        } else {
          tempList.push(previousPoint)
        }
      })
      return tempList
    },
    tempRightSeries() {
      const tempList = []
      let previousPoint = 0
      const uniqueList = uniqBy(this.filteredProbeData, 'tsProbeRegistry')
      forEach(uniqueList, (item) => {
        if (item["strAmbienceName"] === "TD") {
          previousPoint = item["flProbeAvg"]
          tempList.push(item["flProbeAvg"])
        } else {
          tempList.push(previousPoint)
        }
      })
      return tempList
    },
    tempExternalSeries() {
      const tempList = []
      let previousPoint = 0
      const uniqueList = uniqBy(this.filteredProbeData, 'tsProbeRegistry')
      forEach(uniqueList, (item) => {
        if (item["strAmbienceName"] === "T_EXTERNA") {
          previousPoint = item["flProbeAvg"]
          tempList.push(item["flProbeAvg"])
        } else {
          tempList.push(previousPoint)
        }
      })
      return tempList
    },
    tempInternalSeries() {
      const tempList = []
      let previousPoint = 0
      const uniqueList = uniqBy(this.filteredProbeData, 'tsProbeRegistry')
      forEach(uniqueList, (item) => {
        if (item["strAmbienceName"] === "T_INTERNA") {
          previousPoint = item["flProbeAvg"]
          tempList.push(item["flProbeAvg"])
        } else {
          tempList.push(previousPoint)
        }
      })
      return tempList
    },
    series() {
      return [
        {
          name: "Temperatura Máxima",
          type: "line",
          data: this.tempMaxSeries
        },
        {
          name: "Temperatura Externa",
          type: "line",
          data: this.tempExternalSeries
        },
        {
          name: "Temperatura Esquerda",
          type: "line",
          data: this.tempLeftSeries
        },
        {
          name: "Temperatura Direita",
          type: "line",
          data: this.tempRightSeries
        },
        {
          name: "Temperatura Mínima",
          type: "line",
          data: this.tempMinSeries
        },
        {
          name: "Temperatura Média Interna",
          type: "line",
          data: this.tempInternalSeries
        }
      ]
    },
    
    categories() {
      const dateList = this.filteredProbeData.map(item => item["tsProbeRegistry"])
      return uniq(dateList).sort((a, b) => new Date(a) - new Date(b))
    },
    chartOptions() {
      const colors = ["#10303D", "#9575CD", "#2196F3", "#4CAF50", "#FF5429", "#ff00f1"]
      const stroke = {
        curve: "straight",
        dashArray: [5, 0, 0, 0, 5, 0],
        width: [4, 2, 2, 2, 4, 2],
        colors: ["#10303D", "#9575CD", "#2196F3", "#4CAF50", "#FF5429", "#ff00f1"]
      }
      const markers = {
        size: [1, 6, 6, 6, 1, 6]
      }
      const xaxis = {
        type: "datetime",
        categories: this.categories,
        title: {
          text: "Data/Hora da Medição",
          offsetY: 10
        },
        labels: {
          style: {
            colors: "#000000de"
          },
        }
      }
      const yaxis = {
        labels: {
          style: {
            color: "#000000de"
          },
          formatter: function (value) {
            return value + " °C"
          }
        }
      }
      const legend = {
        position: "top",
        horizontalAlign: "center",
        fontSize: "14px",
        labels: {
          useSeriesColors: false
        }
      }
      const toolbar = {
        show: true,
        tools: {
          download: true,
          selection: true
        }
      }
      return {
        colors: colors,
        stroke: stroke,
        markers: markers,
        xaxis: xaxis,
        yaxis: yaxis,
        legend: legend,
        toolbar: toolbar,
      }
    },
    
    tableHeaders() {
      return [
        {text: "", value: "color", sortable: false, align: "end", width: 8},
        {text: "Data", value: "tsProbeRegistry", sortable: false},
        {text: "Temperatura", value: "strAmbienceName", sortable: false, align: "start"},
        {text: "Mínima", value: "flProbeMin", sortable: false},
        {text: "Máxima", value: "flProbeMax", sortable: false},
        {text: "Média", value: "flProbeAvg", sortable: false},
      ]
    }
  },
  methods: {
    getTempColor(value) {
      switch (value) {
        case "TE":
          return "#2196F3"
        case "TD":
          return "#4CAF50"
        case "T_EXTERNA":
          return "#9575CD"
        case "T_INTERNA":
          return "#10303D"
        default:
          return "#ff5429"
      }
    },
  }
}
</script>

<style scoped>
</style>